import { Injectable, inject, signal } from '@angular/core';
import { AppStateResetService } from '@core/state/app-state-reset.service';
import { BreadcrumbItem } from '@shared/models/breadcrumb-item.model';

/** What a phrase written with data is filled with, e.g. the day and month of an invoice. */
type PhraseParams = Readonly<Record<string, string | number>>;

@Injectable({ providedIn: 'root' })
export class ShellStateService {
  private readonly appStateReset = inject(AppStateResetService);

  private readonly _collapsed = signal(false);
  private readonly _drawerOpen = signal(false);
  private readonly _title = signal('');
  private readonly _phrase = signal('');
  private readonly _phraseParams = signal<PhraseParams | undefined>(undefined);
  private readonly _lastUpdate = signal<Date | null>(null);
  private readonly _breadcrumb = signal<BreadcrumbItem[] | null>(null);

  readonly collapsed = this._collapsed.asReadonly();
  readonly drawerOpen = this._drawerOpen.asReadonly();
  readonly title = this._title.asReadonly();
  readonly phrase = this._phrase.asReadonly();
  readonly phraseParams = this._phraseParams.asReadonly();
  readonly lastUpdate = this._lastUpdate.asReadonly();
  readonly breadcrumb = this._breadcrumb.asReadonly();

  constructor() {
    this.appStateReset.register(() => this.reset());
  }

  toggleCollapsed(): void {
    this._collapsed.update((v) => !v);
  }

  toggleDrawerOpen(): void {
    this._drawerOpen.update((v) => !v);
  }

  setDrawerOpen(value: boolean): void {
    this._drawerOpen.set(value);
  }

  setTitle(value: string): void {
    this._title.set(value);
  }

  /**
   * `params` fill the placeholders of a phrase written with data, e.g. an invoice date. A phrase
   * set without them clears the ones the previous page left.
   */
  setPhrase(value: string, params?: PhraseParams): void {
    this._phrase.set(value);
    this._phraseParams.set(params);
  }

  // Só a data que vem do payload; o relógio local não diz quando o dado foi gerado.
  setLastUpdate(value: Date | null): void {
    this._lastUpdate.set(value);
  }

  // Passe `null` nas telas de primeiro nível, que não têm trilha - só o título.
  setBreadcrumb(value: BreadcrumbItem[] | null): void {
    this._breadcrumb.set(value);
  }

  /**
   * Puts the shell back to how it opens for a fresh session — the sidebar layout
   * included, since it is a preference of the user who just left.
   */
  reset(): void {
    this._collapsed.set(false);
    this._drawerOpen.set(false);
    this._title.set('');
    this._phrase.set('');
    this._phraseParams.set(undefined);
    this._lastUpdate.set(null);
    this._breadcrumb.set(null);
  }
}
